import { fetchData } from 'Utils/fetch-data';
import { BaseApi } from './base-api';
import type {
  AchievementProgressI,
  AssignableCourseI,
  CourseEmployeeI,
  CourseEmployeeFilterI,
  DevelopmentPlanBucketsI,
  DevelopmentPlanEntryI,
  EmployeeProfileI,
  UserI,
  UserFilterI,
  GetUserCoursesQuery,
  GetUserCoursesResponse,
  UsersQuery,
  Users,
} from 'types';

class EmployeesApi extends BaseApi<UserI, UserFilterI> {
  /**
   * Obtiene todos los empleados que pertenecen a un curso en específico.
   * Este método se utiliza para obtener a los empleados al momento de revisar la evaluación
   * manual de un curso.
   *
   * @see {@link src/Hooks/useCoursesLessonsPage}
   * @param courseId - ID del curso.
   * @param filter - Filtros para obtener los empleados.
   */
  async readEmployeesByCourseId(courseId: string, lessonId: string, filter: CourseEmployeeFilterI = {}) {
    const response = await fetchData<CourseEmployeeI[]>(`/courses/${courseId}/lessons/${lessonId}/employees`, {
      method: 'GET',
      params: filter,
    });
    return response;
  }

  /**
   * Obtiene el plan de desarrollo de un empleado. Es decir, los cursos
   * que tiene asignados en el sistema.
   * @param userId - ID del empleado.
   */
  async readDevelopmentPlan(userId: string) {
    const response = await fetchData<DevelopmentPlanBucketsI>(`${this.endpoint}/${userId}/development-plan`, {
      method: 'GET',
    });
    return response;
  }

  /**
   * `GET /employees/:id`
   *
   * Ficha del empleado. Sustituye a la consulta `User_findById` de GraphQL y al
   * arreglo posicional que devolvía `Api/employeeAsess.js`.
   */
  async readProfile(employeeId: string) {
    return fetchData<EmployeeProfileI>(`${this.endpoint}/${employeeId}`, { method: 'GET' });
  }

  /**
   * `GET /employees/:id/achievements`
   *
   * Los cuatro logros en una llamada. Antes eran cuatro consultas en paralelo
   * que se casaban con su texto por la posición del arreglo.
   */
  async readAchievements(employeeId: string) {
    return fetchData<AchievementProgressI[]>(`${this.endpoint}/${employeeId}/achievements`, { method: 'GET' });
  }

  /** `POST /employees/:id/courses` */
  async assignCourses(employeeId: string, courseIds: string[]) {
    return fetchData<{ assigned: number; courseIds: string[] }>(`${this.endpoint}/${employeeId}/courses`, {
      method: 'POST',
      data: { courseIds },
    });
  }

  /** `DELETE /employees/:id/courses/:courseId` */
  async unassignCourse(employeeId: string, courseId: string) {
    return fetchData<{ success: boolean }>(`${this.endpoint}/${employeeId}/courses/${courseId}`, {
      method: 'DELETE',
    });
  }

  /** `PATCH /employees/:id/role` */
  async changeRole(employeeId: string, roleId: string) {
    return fetchData<{ success: boolean }>(`${this.endpoint}/${employeeId}/role`, {
      method: 'PATCH',
      data: { roleId },
    });
  }

  /**
   * Reinicia el progreso de un curso de un empleado. Se utiliza en el la página de "Plan de desarrollo"
   * para reiniciar el progreso de un curso.
   *
   * @param userId - ID del empleado.
   * @param userCourseId - ID del curso.
   */
  async resetUserCourseProgress(userId: string, userCourseId: string) {
    const response = await fetchData<boolean>(
      `${this.endpoint}/${userId}/user-courses/${userCourseId}/actions/reset-progress`,
      {
        method: 'POST',
      }
    );
    return response;
  }

  /**
   * `GET /employees/:id/assignable-courses`
   *
   * Cursos publicados que el empleado todavía no tiene. Sustituye a la rama
   * `assigned=false` de `GET /employees/:userId/courses`, que ofrecía también
   * los borradores y devolvía el curso duplicado en plano y anidado.
   */
  async readAssignableCourses(employeeId: string) {
    return fetchData<AssignableCourseI[]>(`${this.endpoint}/${employeeId}/assignable-courses`, { method: 'GET' });
  }

  /**
   * `GET /employees/:id/optional-courses`
   *
   * Cursos opcionales a los que el empleado tiene derecho y no tiene
   * inscritos. Devuelve la misma forma que el catálogo de asignación.
   */
  async readAvailableOptionalCourses(employeeId: string) {
    return fetchData<AssignableCourseI[]>(`${this.endpoint}/${employeeId}/optional-courses`, { method: 'GET' });
  }

  /**
   * `POST /employees/:id/optional-courses/:courseId/enrollment`
   *
   * Crea la inscripción para poder acreditar el curso. Es idempotente y
   * devuelve la entrada del plan ya lista para el cajón de aprobación.
   */
  async enrollOptionalCourse(employeeId: string, courseId: string) {
    return fetchData<DevelopmentPlanEntryI>(`${this.endpoint}/${employeeId}/optional-courses/${courseId}/enrollment`, {
      method: 'POST',
    });
  }

  /**
   * `GET /employees/:userId/courses?assigned=true|false&enrolled=false`
   *
   * Obtiene los cursos que tiene asignado el empleado, aunque puedes
   * específicar para obtener los que no tiene.
   *
   * @param {string} userId
   * @param {GetUserCoursesQuery} options
   */
  async getCourses(userId: string, { assigned = true, enrolled = false }) {
    const url = `${this.endpoint}/${userId}/courses`;
    const params: GetUserCoursesQuery = {};

    if (assigned !== undefined) {
      params.assigned = assigned;
    }
    if (enrolled !== undefined) {
      params.enrolled = enrolled;
    }

    const response = await fetchData<GetUserCoursesResponse[]>(url, {
      method: 'GET',
      params,
    });

    return response;
  }

  /**
   * Actualiza el estatus de un usuario.
   * @param userId - ID del usuario.
   * @param status - true para activar, false para inactivar.
   */
  async updateStatus(userId: string, status: boolean) {
    const response = await fetchData<{ id: string; status: boolean }>(`${this.endpoint}/${userId}`, {
      method: 'PATCH',
      data: { status },
    });
    return response;
  }

  /**
   * Obtiene a los empleados del sistema.
   */
  async getUsers(params: UsersQuery) {
    const url = `${this.endpoint}/`;
    const response = await fetchData<Users['data']>(url, {
      method: 'GET',
      params,
      includePagination: true,
    });
    return response;
  }
}

const employeesApi = new EmployeesApi('/employees');

export default employeesApi;
